import { action, Action, Thunk, thunk } from "easy-peasy";
import { Injections } from "../../../store";
import { StoreModel } from "../..";
import { IListResponse, IMultiLanguage } from "..";
import { IncidentType } from "../Incidents";
import { SearchableStringFilterInput } from "../../../api/graphql/types";

export interface Checkpoint {
  id: number;
  title: IMultiLanguage;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  type: CheckpointType;
}

export interface CheckpointType {
  id: number;
  title: IMultiLanguage;
  enabled: boolean;
  checkpoints: Checkpoint[];
  type?: IncidentType;
  created_at: string;
  updated_at: string;
}

export interface RoundForm {
  id: number;
  title: IMultiLanguage;
  description?: IMultiLanguage;
  location?: IMultiLanguage;
  enabled: boolean;
  checkpoints: Checkpoint[];
  building: {
    id: string;
  };
  user?: {
    id: string;
    last_name: string;
    first_name: string;
    email: string;
  };
  back_office_user?: {
    id: string;
    last_name: string;
    first_name: string;
    email: string;
  };
  created_at: string;
  updated_at: string;
}

export interface CheckpointRating {
  id: number;
  value: number;
  checkpoint: Checkpoint;
  created_at: string;
  updated_at: string;
}

export interface Round {
  id: number;
  status: string;
  form: RoundForm;
  ratings: CheckpointRating[];
  average_rating: number;
  user: {
    id: string;
    last_name: string;
    first_name: string;
    email: string;
  };
  created_at: string;
  updated_at: string;
}

export type SearchableRoundFormsFilterInput = {
  building_id?: SearchableStringFilterInput;
};

export type SearchableRoundsFilterInput = {
  building_id?: SearchableStringFilterInput;
};

export type SearchableCheckpointTypesFilterInput = {
  building_id?: SearchableStringFilterInput;
};

export interface DeleteCheckpointTypeOpts {
  id: number;
}

export interface DeleteRoundFormOpts {
  id: number;
}

export interface DeleteCheckpointOpts {
  id: number;
}

export interface ListCheckpointTypesOpts {
  filter: SearchableCheckpointTypesFilterInput;
  limit: number;
  offset: number;
}

export interface ListRoundFormsOpts {
  filter: SearchableRoundFormsFilterInput;
  limit: number;
  offset: number;
}

export interface ListRoundsOpts {
  filter: SearchableRoundsFilterInput;
  limit: number;
  offset: number;
}

export interface ListCheckpointsOpts {
  checkpointTypeId: string;
  limit: number;
  offset: number;
}

export interface CreateRoundFormOpts {
  building_id: string;
  checkpoints: number[];
  title: IMultiLanguage;
  description: IMultiLanguage;
  location: IMultiLanguage;
  enabled: boolean;
}

export interface CreateCheckpointTypeOpts {
  building_id: string;
  title: IMultiLanguage;
  enabled: boolean;
}

export interface CreateCheckpointOpts {
  title: IMultiLanguage;
  enabled: boolean;
}

export interface UpdateRoundFormOpts {
  id: number;
  checkpoints?: number[];
  title?: IMultiLanguage;
  description?: IMultiLanguage;
  location?: IMultiLanguage;
  enabled?: boolean;
}

export interface UpdateCheckpointTypeOpts {
  id: number;
  title?: IMultiLanguage;
  enabled?: boolean;
}

export interface UpdateCheckpointOpts {
  title?: IMultiLanguage;
  enabled?: boolean;
}

export interface InspectionModel {
  rounds: {
    items: Round[];
    total: number;
    has_more: boolean;
  };
  roundForms: {
    items: RoundForm[];
    total: number;
    has_more: boolean;
  };
  checkpointTypes: {
    items: CheckpointType[];
    total: number;
    has_more: boolean;
  };
  checkpoints: {
    items: Checkpoint[];
    total: number;
    has_more: boolean;
  };
  round: Round | null;
  roundForm: RoundForm | null;
  checkpointType: CheckpointType | null;
  checkpoint: Checkpoint | null;

  setRound: Action<InspectionModel, Round | null>;
  setRoundForm: Action<InspectionModel, RoundForm | null>;
  setCheckpointType: Action<InspectionModel, CheckpointType | null>;
  setCheckpoint: Action<InspectionModel, Checkpoint | null>;
  setRounds: Action<
    InspectionModel,
    {
      items: Round[];
      total: number;
      has_more: boolean;
    }
  >;
  setRoundForms: Action<
    InspectionModel,
    {
      items: RoundForm[];
      total: number;
      has_more: boolean;
    }
  >;
  setCheckpointTypes: Action<
    InspectionModel,
    { items: CheckpointType[]; total: number; has_more: boolean }
  >;
  setCheckpoints: Action<
    InspectionModel,
    { items: Checkpoint[]; total: number; has_more: boolean }
  >;
  removeRoundForm: Action<InspectionModel, DeleteRoundFormOpts>;
  removeCheckpointType: Action<InspectionModel, DeleteCheckpointTypeOpts>;
  removeCheckpoint: Action<InspectionModel, DeleteCheckpointOpts>;
  listRounds: Thunk<InspectionModel, ListRoundsOpts, Injections, StoreModel>;
  listRoundForms: Thunk<
    InspectionModel,
    ListRoundFormsOpts,
    Injections,
    StoreModel
  >;
  listCheckpointTypes: Thunk<
    InspectionModel,
    ListCheckpointTypesOpts,
    Injections,
    StoreModel
  >;
  listCheckpoints: Thunk<
    InspectionModel,
    ListCheckpointsOpts,
    Injections,
    StoreModel
  >;
  getRound: Thunk<InspectionModel, { id: string }, Injections, StoreModel>;
  getRoundForm: Thunk<InspectionModel, { id: string }, Injections, StoreModel>;
  createRoundForm: Thunk<
    InspectionModel,
    CreateRoundFormOpts,
    Injections,
    StoreModel
  >;
  updateRoundForm: Thunk<
    InspectionModel,
    UpdateRoundFormOpts,
    Injections,
    StoreModel
  >;
  deleteRoundForm: Thunk<
    InspectionModel,
    DeleteRoundFormOpts,
    Injections,
    StoreModel
  >;
  getCheckpointType: Thunk<
    InspectionModel,
    { id: string },
    Injections,
    StoreModel
  >;
  createCheckpointType: Thunk<
    InspectionModel,
    CreateCheckpointTypeOpts,
    Injections,
    StoreModel
  >;
  updateCheckpointType: Thunk<
    InspectionModel,
    UpdateCheckpointTypeOpts,
    Injections,
    StoreModel
  >;
  deleteCheckpointType: Thunk<
    InspectionModel,
    DeleteCheckpointTypeOpts,
    Injections,
    StoreModel
  >;
  getCheckpoint: Thunk<
    InspectionModel,
    { checkpointTypeId: string; checkpointId: string },
    Injections,
    StoreModel
  >;
  createCheckpoint: Thunk<
    InspectionModel,
    { checkpointTypeId: string; input: CreateCheckpointOpts },
    Injections,
    StoreModel
  >;
  updateCheckpoint: Thunk<
    InspectionModel,
    {
      checkpointTypeId: string;
      checkpointId: string;
      input: UpdateCheckpointOpts;
    },
    Injections,
    StoreModel
  >;
  deleteCheckpoint: Thunk<
    InspectionModel,
    { checkpointTypeId: string; checkpointId: number },
    Injections,
    StoreModel
  >;
}

export const inspectionModel: InspectionModel = {
  rounds: { items: [], total: 0, has_more: false },
  roundForms: { items: [], total: 0, has_more: false },
  checkpointTypes: { items: [], total: 0, has_more: false },
  checkpoints: { items: [], total: 0, has_more: false },

  round: null,
  roundForm: null,
  checkpointType: null,
  checkpoint: null,

  setRound: action((state, payload: Round) => {
    state.round = payload;
  }),
  setRoundForm: action((state, payload: RoundForm) => {
    state.roundForm = payload;
  }),
  setCheckpointType: action((state, payload: CheckpointType) => {
    state.checkpointType = payload;
  }),
  setCheckpoint: action((state, payload: Checkpoint) => {
    state.checkpoint = payload;
  }),

  setRounds: action((state, payload) => {
    state.rounds = payload;
  }),
  setRoundForms: action((state, payload) => {
    state.roundForms = payload;
  }),
  setCheckpointTypes: action((state, payload) => {
    state.checkpointTypes = payload;
  }),
  setCheckpoints: action((state, payload) => {
    state.checkpoints = payload;
  }),

  removeRoundForm: action((state, payload) => {
    state.roundForms.items = state.roundForms.items.filter(
      p => p.id !== payload.id
    );
    state.roundForms.total = state.roundForms.total - 1;
  }),
  removeCheckpointType: action((state, payload) => {
    state.checkpointTypes.items = state.checkpointTypes.items.filter(
      p => p.id !== payload.id
    );
    state.checkpointTypes.total = state.checkpointTypes.total - 1;
  }),
  removeCheckpoint: action((state, payload) => {
    state.checkpoints.items = state.checkpoints.items.filter(
      p => p.id !== payload.id
    );
    state.checkpoints.total = state.checkpoints.total - 1;
  }),

  getRound: thunk(async (actions, payload: { id: string }, { injections }) => {
    const { inspectionService } = injections;
    const response = await inspectionService.getRound(payload.id);
    if (response.status === 200 && response.data.success) {
      actions.setRound(response.data.data);
    }
  }),
  listRounds: thunk(
    async (actions, payload: ListRoundsOpts, { injections }) => {
      const { inspectionService } = injections;
      const response = await inspectionService.listRounds(payload);
      if (response.status === 200 && response.data.success) {
        const rounds: IListResponse<Round> = response.data;
        actions.setRounds(rounds.data);
      }
    }
  ),

  getRoundForm: thunk(
    async (actions, payload: { id: string }, { injections }) => {
      const { inspectionService } = injections;
      const response = await inspectionService.getRoundForm(payload.id);
      if (response.status === 200 && response.data.success) {
        actions.setRoundForm(response.data.data);
      }
    }
  ),
  listRoundForms: thunk(
    async (actions, payload: ListRoundFormsOpts, { injections }) => {
      const { inspectionService } = injections;
      const response = await inspectionService.listRoundForms(payload);
      if (response.status === 200 && response.data.success) {
        const roundForms: IListResponse<RoundForm> = response.data;
        actions.setRoundForms(roundForms.data);
      }
    }
  ),
  createRoundForm: thunk(
    async (actions, payload: CreateRoundFormOpts, { injections }) => {
      const { inspectionService } = injections;
      const response = await inspectionService.createRoundForm(payload);
      if (response.status === 201 && response.data.success) {
        actions.setRoundForm(response.data.data);
      }
    }
  ),
  updateRoundForm: thunk(
    async (actions, payload: UpdateRoundFormOpts, { injections, getState }) => {
      const { inspectionService } = injections;

      const response = await inspectionService.updateRoundForm(payload);

      if (response.status === 200 && response.data.success) {
        const data = response.data.data;
        actions.setRoundForm(data);

        const state = getState();
        const index = state.roundForms.items.findIndex(p => p.id === data.id);

        if (index !== -1) {
          const newItems = [...state.roundForms.items];
          newItems.splice(index, 1, data);

          actions.setRoundForms({
            ...state.roundForms,
            items: newItems,
          });
        }
      }

      return response.data;
    }
  ),
  deleteRoundForm: thunk(
    async (actions, payload: DeleteRoundFormOpts, { injections }) => {
      const { inspectionService } = injections;
      const response = await inspectionService.deleteRoundForm(payload.id);
      if (response.status === 200 && response.data.success) {
        actions.setRoundForm(null);
        actions.removeRoundForm(payload);
      }
    }
  ),

  getCheckpointType: thunk(
    async (actions, payload: { id: string }, { injections }) => {
      const { inspectionService } = injections;
      const response = await inspectionService.getCheckpointType(payload.id);
      if (response.status === 200 && response.data.success) {
        actions.setCheckpointType(response.data.data);
      }
    }
  ),
  listCheckpointTypes: thunk(
    async (actions, payload: ListCheckpointTypesOpts, { injections }) => {
      const { inspectionService } = injections;
      const response = await inspectionService.listCheckpointTypes(payload);
      if (response.status === 200 && response.data.success) {
        const types: IListResponse<CheckpointType> = response.data;
        actions.setCheckpointTypes(types.data);

        return types.data;
      }
    }
  ),
  createCheckpointType: thunk(
    async (actions, payload: CreateCheckpointTypeOpts, { injections }) => {
      const { inspectionService } = injections;
      const response = await inspectionService.createCheckpointType(payload);
      if (response.status === 201 && response.data.success) {
        actions.setCheckpointType(response.data.data);
      }
    }
  ),
  updateCheckpointType: thunk(
    async (
      actions,
      payload: UpdateCheckpointTypeOpts,
      { injections, getState }
    ) => {
      const { inspectionService } = injections;

      const response = await inspectionService.updateCheckpointType(payload);

      if (response.status === 200 && response.data.success) {
        const data = response.data.data;
        actions.setCheckpointType(data);

        const state = getState();
        const index = state.checkpointTypes.items.findIndex(
          p => p.id === data.id
        );

        if (index !== -1) {
          const newItems = [...state.checkpointTypes.items];
          newItems.splice(index, 1, data);

          actions.setCheckpointTypes({
            ...state.checkpointTypes,
            items: newItems,
          });
        }
      }

      return response.data;
    }
  ),
  deleteCheckpointType: thunk(
    async (actions, payload: DeleteCheckpointTypeOpts, { injections }) => {
      const { inspectionService } = injections;
      const response = await inspectionService.deleteCheckpointType(payload.id);
      if (response.status === 200 && response.data.success) {
        actions.setCheckpointType(null);
        actions.removeCheckpointType(payload);
      }
    }
  ),

  getCheckpoint: thunk(
    async (
      actions,
      payload: { checkpointTypeId: string; checkpointId: string },
      { injections }
    ) => {
      const { inspectionService } = injections;
      const response = await inspectionService.getCheckpoint(
        payload.checkpointTypeId,
        payload.checkpointId
      );
      if (response.status === 200 && response.data.success) {
        actions.setCheckpoint(response.data.data);
      }
    }
  ),
  listCheckpoints: thunk(
    async (actions, payload: ListCheckpointsOpts, { injections }) => {
      const { inspectionService } = injections;
      const response = await inspectionService.listCheckpoints(payload);
      if (response.status === 200 && response.data.success) {
        const checkpoints: IListResponse<Checkpoint> = response.data;
        actions.setCheckpoints(checkpoints.data);

        return checkpoints.data;
      }
    }
  ),
  createCheckpoint: thunk(
    async (
      actions,
      payload: { checkpointTypeId: string; input: CreateCheckpointOpts },
      { injections }
    ) => {
      const { inspectionService } = injections;
      const response = await inspectionService.createCheckpoint(
        payload.checkpointTypeId,
        payload.input
      );
      if (response.status === 201 && response.data.success) {
        actions.setCheckpoint(response.data.data);
      }
    }
  ),
  updateCheckpoint: thunk(
    async (
      actions,
      payload: {
        checkpointTypeId: string;
        checkpointId: string;
        input: UpdateCheckpointOpts;
      },
      { injections, getState }
    ) => {
      const { inspectionService } = injections;

      const response = await inspectionService.updateCheckpoint(
        payload.checkpointTypeId,
        payload.checkpointId,
        payload.input
      );

      if (response.status === 200 && response.data.success) {
        const data = response.data.data;
        actions.setCheckpoint(data);

        const state = getState();
        const index = state.checkpoints.items.findIndex(p => p.id === data.id);

        if (index !== -1) {
          const newItems = [...state.checkpoints.items];
          newItems.splice(index, 1, data);

          actions.setCheckpoints({
            ...state.checkpoints,
            items: newItems,
          });
        }
      }

      return response.data;
    }
  ),
  deleteCheckpoint: thunk(
    async (
      actions,
      payload: { checkpointTypeId: string; checkpointId: number },
      { injections }
    ) => {
      const { inspectionService } = injections;
      const response = await inspectionService.deleteCheckpoint(
        payload.checkpointTypeId,
        payload.checkpointId
      );
      if (response.status === 200 && response.data.success) {
        actions.setCheckpoint(null);
        actions.removeCheckpoint({ id: payload.checkpointId });
      }
    }
  ),
};
